import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import Grid from '@mui/material/Grid'
import MuiLink from '@mui/material/Link'
import Badge from '@mui/material/Badge'
import useClasses from '../hooks/UseClasses'
import { IconButton, useTheme, Typography, AppBar, Box, Avatar, Menu, MenuItem, Divider, Toolbar } from '@mui/material'
import Backdrop from '@mui/material/Backdrop'
import CallUsIcon from '../icons/mobile/CallUsIcon'
import LiveChatIcon from '../icons/mobile/LiveChatIcon'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import profileConfig from '@/config/constants/profile/config_pontio.js'
import config from '@/config/env'
import CustomSvgIcon from '@/components/icons/builder/CustomSvgIcon'
import crossIcon from '@/components/icons/builder/paths/crossIcon'
import { useEmbeddedMessaging } from '../app-context/EmbeddedMessagingContext'
import { useTableQueryingContext } from '../app-context/TableQueryContext'
import MenuBar from './features/MenuBar'
import { useAuth } from '../app-context/AuthContext'
import { useNavigate } from 'react-router-dom'
import logoutIcon from '../icons/builder/paths/logoutIcon'
import menuMobileIcon from '../icons/builder/paths/menuMobileIcon'
import { useIsFinalCustomer } from '@/config/utils/RolesUtils'
import CtmCalculator from '@/containers/pages/customer/calculator/CtmCalculator'
import calculateIcon from '../icons/builder/paths/calculateIcon'
import WrapperComponentControl from '../permission-control/WrapperComponentControl'
import rolesConstants from '@/config/constants/RolesConstants'

const { CRISP_CHAT, SF_ENABLED } = config

const styles = (theme) => ({
  link: {
    textDecoration: 'none !important',
    display: 'grid',
    gap: '0.5rem',
    justifyContent: 'center',
  },
  card: {
    backgroundColor: theme.palette.brandless.background.white.color,
    padding: '1rem 0',
    borderRadius: '0.625rem',
    display: 'grid',
    gap: '0.5rem',
    justifyItems: 'center',
  },
})

const HeaderMobile = ({ standard = true }) => {
  const user = useSelector(({ login }) => login)
  const customerServiceTel = profileConfig.customerServiceTel
  const [openHelp, setOpenHelp] = useState(false)
  const [openChat, setOpenChat] = useState(false)
  const [openMenuDrawer, setOpenMenuDrawer] = useState(false)
  const [chatResponse, setChatResponse] = useState(0)
  const { removeQuerying } = useTableQueryingContext()
  const isFinalCustomer = useIsFinalCustomer()
  const { logout } = useAuth()
  const [anchorEl, setAnchorEl] = useState(null)
  const openUserMenu = Boolean(anchorEl)
  const { t } = useTranslation()
  const navigate = useNavigate()
  const theme = useTheme()
  const em = useEmbeddedMessaging()
  const classes = useClasses(styles)

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget)
  }

  const handleMenuClick = () => {
    removeQuerying()
    setOpenMenuDrawer(!openMenuDrawer)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleLogout = async () => {
    await logout()
    navigate('/')
  }

  useEffect(() => {
    if (CRISP_CHAT !== undefined && CRISP_CHAT === 'true') {
      $crisp.push(['safe', true])
      $crisp.push(['on', 'chat:closed', closeChat])
      $crisp.push(['on', 'message:received', (message) => crispAnswer(message)])
      $crisp.push(['do', 'chat:hide'])
      if (user !== undefined && user.iduser !== undefined) {
        if (user.name !== undefined) {
          $crisp.push(['set', 'user:nickname', [user.name]])
        }
        if (user.email !== undefined) {
          $crisp.push(['set', 'user:email', [user.email]])
        }
      }
    }
    if (SF_ENABLED !== undefined && SF_ENABLED === 'true') {
      if (user.email !== undefined) {
        em.setHiddenPrechatFields({
          user_email: user.email,
        })
      }
      em.hide()
    }
  }, [user?.email, em])

  const toggleChat = () => {
    if (!openChat) {
      setOpenChat(true)
      setOpenHelp(false)
      if (CRISP_CHAT !== undefined && CRISP_CHAT === 'true') {
        $crisp.push(['do', 'chat:show'])
        $crisp.push(['do', 'chat:open'])
      } else if (SF_ENABLED !== undefined && SF_ENABLED === 'true') {
        em.show()
        em.launchChat()
      }
      setChatResponse(0)
    } else {
      closeChat()
    }
  }

  const closeChat = () => {
    setOpenHelp(false)
    if (CRISP_CHAT !== undefined && CRISP_CHAT === 'true') {
      $crisp.push(['do', 'chat:hide'])
    } else if (SF_ENABLED !== undefined && SF_ENABLED === 'true') {
      em.hide()
    }
    setOpenChat(false)
  }

  const crispAnswer = () => {
    if (!openChat) {
      setChatResponse((currChatResponse) => currChatResponse + 1)
    }
  }

  return (
    <>
      <AppBar
        position="static"
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          height: theme.mobileHeaderHeight,
          backgroundColor: theme.palette.brandless.background.blueLight.color,
        }}>
        <Toolbar
          disableGutters
          sx={{
            minHeight: `${theme.mobileHeaderHeight}px !important`,
            height: `${theme.mobileHeaderHeight}px`,
            padding: '0.5rem 1rem',
            display: 'flex',
            justifyContent: 'space-between',
          }}>
          <Box style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            {user?.name && !isFinalCustomer && (
              <IconButton onClick={handleMenuClick}>
                <CustomSvgIcon svgString={menuMobileIcon} fill={theme.palette.brandless.icon.blueMedium.color} />
              </IconButton>
            )}
            <img
              srcSet={standard ? '/assets/Logotipo_Pontio.webp' : '/assets/Logotipo_Fondo-Energia_Azul.webp'}
              style={{ width: standard ? '4.75rem' : '9rem', height: standard ? '1.5rem' : '2rem' }}
              src={standard ? '/assets/Logotipo_Pontio.webp' : '/assets/Logotipo_Fondo-Energia_Azul.webp'}
            />
          </Box>
          <Grid style={{ display: 'flex', gap: '0.5rem' }}>
            <Badge
              color="error"
              badgeContent={chatResponse}
              max={99}
              onClick={() => {
                if (chatResponse > 0) {
                  toggleChat()
                } else {
                  setOpenHelp(true)
                }
              }}>
              <HelpOutlineIcon style={{ color: theme.palette.brandless.icon.blueMedium.color }} />
            </Badge>
            <WrapperComponentControl
              rolesAccess={[
                rolesConstants.ROLE_MERCHANT,
                rolesConstants.ROLE_MERCHANTCOM,
                rolesConstants.ROLE_COMMERCIAL,
                rolesConstants.ROLE_OPERATOR,
                rolesConstants.ROLE_ACCOUNTANT,
                rolesConstants.ROLE_ADMIN,
              ]}>
              <CtmCalculator
                component={
                  <CustomSvgIcon svgString={calculateIcon} fill={theme.palette.brandless.icon.blueMedium.color} />
                }
              />
            </WrapperComponentControl>
            <WrapperComponentControl
              conditions={[
                {
                  condition: user?.name != null,
                },
              ]}>
              <Avatar
                onClick={handleClick}
                style={{
                  width: '1.5rem',
                  height: '1.5rem',
                  backgroundColor: theme.palette.brandless.icon.blueMedium.color,
                }}>
                <Typography variant="body_xxs_medium" style={{ color: theme.palette.brandless.text.white.color }}>
                  {user?.name?.substring(0, 1)}
                </Typography>
              </Avatar>
            </WrapperComponentControl>
          </Grid>
          <Backdrop
            sx={{
              backdropFilter: blur,
              zIndex: (theme) => theme.zIndex.drawer + 1,
            }}
            onClick={() => setOpenHelp(false)}
            open={openHelp}>
            <Grid container style={{ display: 'flex', padding: '0 1rem', gap: '0.5rem' }}>
              <Grid size={12} style={{ textAlign: 'right' }}>
                <IconButton onClick={() => setOpenHelp(false)}>
                  <CustomSvgIcon
                    svgString={crossIcon}
                    width="1.8rem"
                    height="1.8rem"
                    fill={theme.palette.brandless.icon.white.color}
                    style={{ marginTop: '0.5rem' }}
                  />
                </IconButton>
              </Grid>
              <Grid size={12} className={classes.card} onClick={(event) => event.stopPropagation()}>
                <MuiLink className={classes.link} href={'tel:' + customerServiceTel}>
                  <CallUsIcon style={{ width: '4.375rem', height: '4.375rem' }} />
                  <Typography variant={'body_s_medium'}>{t('CALLUS')}</Typography>
                </MuiLink>
              </Grid>
              <Grid size={12} className={classes.card} onClick={(event) => event.stopPropagation()}>
                <Badge
                  color="error"
                  badgeContent={chatResponse}
                  max={99}
                  style={{ cursor: 'pointer', marginLeft: '0.5rem' }}>
                  <LiveChatIcon style={{ width: '4.375rem', height: '4.375rem' }} onClick={() => toggleChat()} />
                </Badge>
                <Typography variant={'body_s_medium'}>{t('LIVECHAT')}</Typography>
              </Grid>
            </Grid>
          </Backdrop>
        </Toolbar>
      </AppBar>
      {openMenuDrawer && (
        <MenuBar open={openMenuDrawer} setOpen={handleMenuClick} headerHeight={theme.mobileHeaderHeight} />
      )}
      {openUserMenu && (
        <Menu
          id="header"
          anchorEl={anchorEl}
          keepMounted
          open={openUserMenu}
          onClose={handleClose}
          slotProps={{
            paper: { style: { maxHeight: 48 * 4.5, boxShadow: theme.customShadows.medium } },
            list: { disablePadding: true },
          }}>
          <Grid style={{ display: 'grid', gap: '0.75rem' }}>
            <Grid style={{ padding: '1rem 1rem 0' }}>
              <Typography variant="body_xs_medium">{user?.name}</Typography>
            </Grid>
            <Divider />
            <Grid style={{ padding: '0 1rem 1rem' }}>
              <MenuItem
                onClick={() => handleLogout()}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0',
                  minHeight: 'min-content',
                }}>
                <CustomSvgIcon
                  svgString={logoutIcon}
                  fill={theme.palette.brandless.icon.black.color}
                  width="1.25rem"
                  height="1.25rem"
                />
                <Typography variant="body_xs_medium">{t('HEADER.LOGOUT')}</Typography>
              </MenuItem>
            </Grid>
          </Grid>
        </Menu>
      )}
    </>
  )
}

export default HeaderMobile
